/**
 * Utility functions for sorting filter options alphabetically
 */

/**
 * Sorts filter options alphabetically by label
 * @param {Array} options - Array of filter options with {label, value, count} structure
 * @returns {Array} Sorted array of options
 */
export function sortFilterOptions(options) {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.sort((a, b) => {
    const labelA = (a.label || a.value || '').toString().toLowerCase();
    const labelB = (b.label || b.value || '').toString().toLowerCase();
    return labelA.localeCompare(labelB);
  });
}

/**
 * Sorts multiple filter option sets alphabetically
 * @param {Object} filterOptions - Object with filterType keys and option arrays as values
 * @returns {Object} Object with all option arrays sorted alphabetically
 */
export function sortAllFilterOptions(filterOptions) {
  if (!filterOptions || typeof filterOptions !== 'object') {
    return {};
  }

  const sortedOptions = {};

  Object.entries(filterOptions).forEach(([filterType, options]) => {
    sortedOptions[filterType] = sortFilterOptions(options);
  });

  return sortedOptions;
}

/**
 * Creates a sorted mineral option with category filters at the top
 * @param {Array} criticalMinerals - Array of critical mineral options
 * @param {Array} nonCriticalMinerals - Array of non-critical mineral options
 * @returns {Array} Combined and sorted mineral options with category filters first
 */
export function createSortedMineralOptions(criticalMinerals, nonCriticalMinerals) {
  // Category options always come first
  const categoryOptions = [
    {
      label: 'Critical Minerals Only',
      value: 'critical-minerals-only',
      category: 'critical'
    },
    {
      label: 'Non-Critical Minerals Only',
      value: 'non-critical-minerals-only',
      category: 'non-critical'
    }
  ];

  // Combine all individual minerals and sort them
  const allMinerals = [
    ...(criticalMinerals || []),
    ...(nonCriticalMinerals || [])
  ];

  const sortedMinerals = sortFilterOptions(allMinerals);

  // Return category options first, then sorted individual minerals
  return [...categoryOptions, ...sortedMinerals];
}